import React, { useEffect, useState } from 'react';
import './EngagementPlanning.css';

const EngagementPlanning = ({ engagementId, onBack, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);
  const [riskRows, setRiskRows] = useState([]);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState('');
  const [riskRunning, setRiskRunning] = useState(false);
  const [acceptedIds, setAcceptedIds] = useState(new Set());
  const [editingRow, setEditingRow] = useState(null);
  const [confirmRerunOpen, setConfirmRerunOpen] = useState(false);
  const [confirmRerunMessage, setConfirmRerunMessage] = useState('');
  const [editValues, setEditValues] = useState({
    assessed_rmm: '',
    response: '',
    comment: ''
  });
  const tscOrder = { CC: 0, A: 1, C: 2, PI: 3, P: 4 };

  const parseTscId = (value) => {
    if (!value) return { order: 99, parts: [], raw: '' };
    const raw = String(value).toUpperCase().trim();
    const match = raw.match(/^(CC|A|C|PI|P)\s*([0-9]+(?:\.[0-9]+)*)/);
    if (match) {
      return {
        order: tscOrder[match[1]] ?? 99,
        parts: match[2].split('.').map((num) => Number(num)),
        raw,
      };
    }
    const prefixMatch = raw.match(/^(CC|A|C|PI|P)/);
    const order = prefixMatch ? tscOrder[prefixMatch[1]] ?? 99 : 99;
    const numeric = raw.replace(/[A-Z]/g, '').trim();
    const parts = numeric
      ? numeric.split('.').map((num) => Number(num)).filter((num) => !Number.isNaN(num))
      : [];
    return { order, parts, raw };
  };

  useEffect(() => {
    const loadComparison = async () => {
      if (!engagementId) return;
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`/api/v1/planning/engagements/${engagementId}/tsc-comparison`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
        });
        if (response.ok) {
          const payload = await response.json();
          setComparison(payload.data);
        } else {
          const err = await response.json();
          setError(err.detail || 'Failed to load TSC comparison');
        }
      } catch (err) {
        setError('Failed to load TSC comparison: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadComparison();
  }, [engagementId]);

  useEffect(() => {
    const loadRiskAssessment = async () => {
      if (!engagementId) return;
      setRiskLoading(true);
      setRiskError('');
      try {
        const response = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
        });
        if (response.ok) {
          const payload = await response.json();
          setRiskRows(payload.data || []);
        } else {
          const err = await response.json();
          setRiskError(err.detail || 'Failed to load risk assessment');
        }
      } catch (err) {
        setRiskError('Failed to load risk assessment: ' + err.message);
      } finally {
        setRiskLoading(false);
      }
    };

    loadRiskAssessment();
  }, [engagementId]);

  const matched = comparison?.matched_controls || [];
  const missing = comparison?.missing_tsc_controls || [];
  const hasMissingControls = missing.length > 0;
  const sortedRiskRows = [...riskRows].sort((a, b) => {
    const left = parseTscId(a.tsc_id);
    const right = parseTscId(b.tsc_id);
    if (left.order !== right.order) return left.order - right.order;
    const len = Math.max(left.parts.length, right.parts.length);
    for (let i = 0; i < len; i += 1) {
      const leftPart = left.parts[i] ?? -1;
      const rightPart = right.parts[i] ?? -1;
      if (leftPart !== rightPart) return leftPart - rightPart;
    }
    return left.raw.localeCompare(right.raw);
  });

  const runRiskAssessment = async (forceRun = false) => {
    const formData = new FormData();
    formData.append('auditor_id', userInfo?.user_id || 'auditor');
    if (forceRun) {
      formData.append('force', 'true');
    }
    return fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment/run`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
      },
      body: formData,
    });
  };

  const executeRiskAssessment = async (forceRun = false) => {
    if (!engagementId) return;
    setRiskRunning(true);
    setRiskError('');
    try {
      const response = await runRiskAssessment(forceRun);
      if (response.ok) {
        const payload = await response.json();
        if (payload.data?.warning && !forceRun) {
          setConfirmRerunMessage(
            'Risk assessment results already exist. Rerunning will delete all results (including accepted). Continue?'
          );
          setConfirmRerunOpen(true);
          return;
        }
        if (payload.data?.items === 0) {
          setRiskError('No risk assessment rows generated. Check inputs.');
        }
        // Refresh list
        const listResp = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
        });
        if (listResp.ok) {
          const listPayload = await listResp.json();
          setRiskRows(listPayload.data || []);
        }
      } else {
        const err = await response.json();
        setRiskError(err.detail || 'Failed to run risk assessment');
      }
    } catch (err) {
      setRiskError('Failed to run risk assessment: ' + err.message);
    } finally {
      setRiskRunning(false);
    }
  };

  const handleRunRiskAssessment = async () => {
    if (!engagementId || riskRunning) return;
    await executeRiskAssessment(false);
  };

  const handleConfirmRerun = async () => {
    setConfirmRerunOpen(false);
    await executeRiskAssessment(true);
  };

  const handleOpenEdit = (row) => {
    setEditingRow(row);
    setEditValues({
      assessed_rmm: row.assessed_rmm || '',
      response: row.response || '',
      comment: row.comment || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    try {
      const response = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment/${editingRow.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: JSON.stringify({
          assessed_rmm_final: editValues.assessed_rmm,
          response_final: editValues.response,
          auditor_comment: editValues.comment,
          status: editingRow.status,
          changed_by: userInfo?.user_id || 'auditor',
          accepted_response_json: { ...editValues }
        })
      });
      if (!response.ok) {
        const err = await response.json();
        setRiskError(err.detail || 'Failed to update risk row');
      } else {
        setEditingRow(null);
        const listResp = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
        });
        if (listResp.ok) {
          const listPayload = await listResp.json();
          setRiskRows(listPayload.data || []);
        }
      }
    } catch (err) {
      setRiskError('Failed to update risk row: ' + err.message);
    }
  };

  const toggleAccept = (rowId) => {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleConfirmRiskAssessment = async () => {
    if (!acceptedIds.size) return;
    try {
      const response = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: JSON.stringify({
          accepted_ids: Array.from(acceptedIds),
          changed_by: userInfo?.user_id || 'auditor'
        })
      });
      if (!response.ok) {
        const err = await response.json();
        setRiskError(err.detail || 'Failed to confirm risk assessment');
      } else {
        setAcceptedIds(new Set());
        const listResp = await fetch(`/api/v1/planning/engagements/${engagementId}/risk-assessment`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
        });
        if (listResp.ok) {
          const listPayload = await listResp.json();
          setRiskRows(listPayload.data || []);
        }
      }
    } catch (err) {
      setRiskError('Failed to confirm risk assessment: ' + err.message);
    }
  };

  return (
    <div className="engagement-planning">
      <div className="planning-header">
        <div className="header-left">
          {onBack && (
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
          )}
          <div>
            <h1>Engagement Planning</h1>
            <p>Planning Phase - Setup and review planning inputs</p>
          </div>
        </div>
        {engagementId && (
          <div className="engagement-meta">
            Engagement: {engagementId}
          </div>
        )}
      </div>

      <div className="planning-body">
        <div className="planning-section">
          <div className="section-header">
            <div>
              <h2>TSC Control Comparison</h2>
              <p>Comparison of in-scope TSC controls vs mapped internal controls.</p>
            </div>
            {comparison && (
              <div className="section-meta">
                <span>Total TSC Controls (Scope): {comparison.reference_count || 0}</span>
                <span>Matched TSC Controls: {comparison.matched_count || 0}</span>
                <span>Missing TSC Controls: {comparison.missing_count || 0}</span>
              </div>
            )}
          </div>

          {loading && <div className="planning-empty">Loading TSC comparison...</div>}
          {!loading && error && <div className="planning-error">⚠️ {error}</div>}
          {!loading && !error && comparison?.message && (
            <div className="planning-empty">⚠️ {comparison.message}</div>
          )}

          {!loading && !error && comparison && matched.length > 0 && (
            <div className="comparison-table">
              <table className="comparison-grid">
                <thead>
                  <tr>
                    <th>TSC ID</th>
                    <th>TSC Statement</th>
                    <th>Control ID</th>
                    <th>Control Statement</th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map((row, idx) => (
                    <tr key={`${row.tsc_id}-${row.control_id}-${idx}`}>
                      <td>{row.tsc_id}</td>
                      <td>{row.tsc_statement}</td>
                      <td>{row.control_id || '-'}</td>
                      <td>{row.control_description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && comparison && missing.length > 0 && (
            <div className="missing-section">
              <h3>Missing TSC Controls</h3>
              <div className="missing-table">
                <table className="comparison-grid">
                  <thead>
                    <tr>
                      <th>TSC ID</th>
                      <th>TSC Statement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missing.map((row) => (
                      <tr key={row.tsc_id}>
                        <td>{row.tsc_id}</td>
                        <td>{row.tsc_statement}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !error && comparison && matched.length === 0 && missing.length === 0 && (
            <div className="planning-empty">No comparison data found.</div>
          )}
        </div>

        <div className="planning-section">
          <div className="section-header">
            <div>
              <h2>Risk Assessment</h2>
              <p>AI assessment of risks per TSC control with auditor confirmation.</p>
            </div>
            <button
              className="btn-run-risk"
              onClick={handleRunRiskAssessment}
              disabled={riskRunning || hasMissingControls}
            >
              {riskRunning ? 'Running...' : 'Run Risk Assessment'}
            </button>
          </div>

          {hasMissingControls && (
            <div className="planning-warning">
              ⚠️ Missing TSC controls detected in comparison. Resolve missing mappings before running risk assessment.
            </div>
          )}

          {riskLoading && <div className="planning-empty">Loading risk assessment...</div>}
          {!riskLoading && riskError && <div className="planning-error">⚠️ {riskError}</div>}

          {!riskLoading && !riskError && riskRows.length === 0 && (
            <div className="planning-empty">No risk assessment results yet.</div>
          )}

          {!riskLoading && !riskError && riskRows.length > 0 && (
            <>
              <div className="comparison-table">
                <table className="comparison-grid risk-grid">
                  <thead>
                    <tr>
                      <th>TSC ID</th>
                      <th>TSC Statement</th>
                      <th>Risk Statement</th>
                      <th>Risk Type</th>
                      <th>Assessed RMM</th>
                      <th>Response</th>
                      <th>AI Rationale</th>
                      <th>Comment</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRiskRows.map((row) => {
                      return (
                        <tr key={row.id}>
                          <td>{row.tsc_id}</td>
                          <td>{row.tsc_statement}</td>
                          <td>{row.risk_statement}</td>
                          <td>{row.risk_type}</td>
                          <td>{row.assessed_rmm || '-'}</td>
                          <td>
                            {row.response
                              ? row.control_id
                                ? `${row.control_id} — ${row.response}`
                                : row.response
                              : '-'}
                          </td>
                          <td>{row.ai_rationale}</td>
                          <td>{row.comment || '-'}</td>
                          <td>
                            <div className="risk-actions">
                              <button className="btn-small" onClick={() => handleOpenEdit(row)}>
                                Edit
                              </button>
                              <button
                                className={`btn-small ${acceptedIds.has(row.id) ? 'active' : ''}`}
                                onClick={() => toggleAccept(row.id)}
                              >
                                {acceptedIds.has(row.id) ? 'Accepted' : 'Accept'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="risk-confirm">
                <button
                  className="btn-confirm"
                  onClick={handleConfirmRiskAssessment}
                  disabled={!acceptedIds.size}
                >
                  Confirm Risk Assessment
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {editingRow && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Risk Assessment</h3>
            <div className="modal-field">
              <label>Assessed RMM</label>
              <select
                className="risk-select"
                value={editValues.assessed_rmm}
                onChange={(e) => setEditValues({ ...editValues, assessed_rmm: e.target.value })}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="modal-field">
              <label>Response</label>
              <textarea
                value={editValues.response}
                onChange={(e) => setEditValues({ ...editValues, response: e.target.value })}
              />
            </div>
            <div className="modal-field">
              <label>Comment</label>
              <textarea
                value={editValues.comment}
                onChange={(e) => setEditValues({ ...editValues, comment: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-small" onClick={() => setEditingRow(null)}>
                Cancel
              </button>
              <button className="btn-small" onClick={handleSaveEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRerunOpen && (
        <div className="modal-overlay">
          <div className="modal-content confirm-modal">
            <h3>Rerun Risk Assessment?</h3>
            <p>{confirmRerunMessage}</p>
            <div className="modal-actions">
              <button className="btn-small" onClick={() => setConfirmRerunOpen(false)}>
                Cancel
              </button>
              <button className="btn-small" onClick={handleConfirmRerun}>
                Rerun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementPlanning;
